import { client } from '../../../../hooks/Client';
import { WIDGET_REGISTRY, WIDGET_REGISTRY_TO_KEY } from '../widgets';
import { BaseWidget } from "../widgets/BaseWidget";
import { PlaceholderWidget } from '../widgets/PlaceholderWidget';

/**
 * @typedef {object} SerializedWidget
 * @property {string|null} docid - Firestore document ID (null for unsaved widgets)
 * @property {string} ID - uuidv4 string
 * @property {string} Type - Widget type (see WIDGET_REGISTRY keys)
 * @property {string} ReportId - Report template ID this widget belongs to
 * @property {string|null} Parent - ID of the parent widget - `null` if root node
 * @property {object} Settings - Widget-specific settings object (see controls)
 * @property {number} Order - Sibling order within parent node
 * @property {string} CompagnyID - ID of the company this widget belongs to
 * @property {boolean} IsTemplate - Denormalized from parent report — required by Firestore rules to allow cross-tenant template reads
 * @property {boolean} [Deleted] - Whether the widget is marked as deleted (used for soft deletion)
 */

/**
 * Serialize a widget to its JSON representation
 * @param {string} reportId
 * @param {BaseWidget} widget
 * @param {boolean} isTemplate - IsTemplate flag of the parent report; denormalized onto the widget so Firestore rules can authorize cross-tenant template reads
 * @returns {SerializedWidget}
 */
export const serializeWidget = (reportId, widget, isTemplate) => {

    const widgetType = WIDGET_REGISTRY_TO_KEY.get(
        // @ts-ignore
        widget.constructor
    );

    if (!widgetType && (widget.constructor !== PlaceholderWidget)) {
        alert('There was a problem saving the report template. Please check the console for details.');
        throw new Error(`Widget type "${widget.constructor.name}" not found in registry.`);
    }

    /**
     * Recursively clean settings by removing undefined values
     * @param {any} settings
     * @returns {any}
     */
    const cleanSettings = (settings) => {
        if (typeof settings !== 'object' || settings === null) {
            return settings;
        }
        if (Array.isArray(settings)) {
            return settings.map(cleanSettings);
        }
        return Object.fromEntries(
            Object.entries(settings)
                .filter(([_, value]) => value !== undefined)
                // @ts-ignore
                .map(([key, value]) => [key, cleanSettings(value)])
        );
    };

    return {
        docid: widget.docid || null,
        ID: widget.id,
        Type: widgetType ?? 'DEPRECATED',
        ReportId: reportId,
        Parent: widget.parent !== null
            ? widget.parent
            : "null",
        Settings: cleanSettings(widget.settings),
        Order: widget.order || 0,
        CompagnyID: client,
        IsTemplate: isTemplate,
        Deleted: widget.deleted || false,
    };
}

/**
 * Instantiate a widget from the serialized JSON / Firebase database
 * @param {SerializedWidget & { docid: string|null }} json - Firebase JSON object
 * @return {BaseWidget}
 */
export const deserializeWidget = (json, editable = false) => {
    let WidgetClass = WIDGET_REGISTRY[json.Type];

    if (!WidgetClass) {
        WidgetClass = PlaceholderWidget;
        console.error(`Widget type "${json.Type}" not found in registry.`, json);
    }

    const instance = new WidgetClass(json.Settings, editable);
    instance.docid = json.docid; // Firestore document ID, used for updates/deletion
    instance.id = json.ID;
    instance.order = Number(json.Order) || 0;
    instance.deleted = Boolean(json.Deleted);
    instance.parent = json.Parent !== "null"
        ? json.Parent
        : null;

    return instance;
}
